import React, { useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import StaffProfiles from '../StaffProfiles';
import RotatingImage from '../RotatingImage';
import MarkdownRenderer from '../MarkdownRenderer';
import type { IActivityWithCourses } from '../../utils/courseSchedule';
import './activityCard.css';

interface ActivityCardProps {
  activity: IActivityWithCourses;
  onSelect: (activity: IActivityWithCourses) => void;
  /**
   * When set, show a per-member enrollment indicator computed from the
   * activity's courses for this member (dashboard member switch).
   */
  highlightMemberId?: number | null;
}

/**
 * Activity poster card — the browse unit for member-facing courses (kroužky).
 * Mirrors the public open-events `.event-card` poster layout: image on the left
 * at full card height, name + rendered-markdown description on the right. The
 * description is clamped to a fixed height — with a bottom fade when, and only
 * when, it actually overflows — so long, formatted content can't break the card
 * layout. The whole card is a button that opens the
 * activity detail modal.
 */
const ActivityCard: React.FC<ActivityCardProps> = ({
  activity,
  onSelect,
  highlightMemberId = null,
}) => {
  const { t } = useTranslation();
  const description = activity.description?.trim() || '';
  const courses = activity.courses || [];
  const courseCount = courses.length;
  const footerHint =
    courseCount > 0
      ? t('activities.viewCourses', { count: courseCount })
      : t('activities.viewDetail');

  // The description is height-clamped in CSS. Only fade its bottom edge when
  // content is actually cut off — an unconditional fade washes out the last
  // line of short descriptions, which reads as a rendering fault.
  const descRef = useRef<HTMLDivElement | null>(null);
  const [isClamped, setIsClamped] = useState(false);

  useLayoutEffect(() => {
    const el = descRef.current;
    if (!el) {
      setIsClamped(false);
      return;
    }
    const measure = () => setIsClamped(el.scrollHeight - el.clientHeight > 1);
    measure();
    // Observe the inner content too: markdown images settle after load and
    // change scrollHeight without resizing the clamped wrapper itself.
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    if (el.firstElementChild) observer.observe(el.firstElementChild);
    return () => observer.disconnect();
  }, [description]);

  // Per-member enrollment indicator: count this member's enrolled/pending
  // courses for the activity (dashboard member switch only).
  const enrolledCount =
    highlightMemberId != null
      ? courses.filter((c) => c.enrolledMemberIds?.includes(highlightMemberId)).length
      : 0;
  const pendingCount =
    highlightMemberId != null
      ? courses.filter((c) => c.pendingMemberIds?.includes(highlightMemberId)).length
      : 0;

  return (
    <article className="activity-with-staff"><button
      type="button"
      className="activity-poster-card"
      onClick={() => onSelect(activity)}
      aria-label={activity.name}
    >
      <div className="activity-poster-card__media">
        <RotatingImage
          coverImage={activity.coverImage}
          gallery={null}
          seed={String(activity._id)}
          alt={activity.name}
        />
      </div>
      <div className="activity-poster-card__body">
        <h3 className="activity-poster-card__name">{activity.name}</h3>
        {description && (
          <div
            ref={descRef}
            className={`activity-poster-card__desc${isClamped ? ' activity-poster-card__desc--clamped' : ''}`}
          >
            <MarkdownRenderer content={description} />
          </div>
        )}
        <div className="activity-poster-card__footer">
          {enrolledCount > 0 ? (
            <span className="activity-poster-card__member-chip activity-poster-card__member-chip--enrolled">
              {t('activities.memberEnrolledCount', { count: enrolledCount })}
            </span>
          ) : pendingCount > 0 ? (
            <span className="activity-poster-card__member-chip activity-poster-card__member-chip--pending">
              {t('activities.memberPendingCount', { count: pendingCount })}
            </span>
          ) : null}
          <span className="activity-poster-card__hint">{footerHint}</span>
        </div>
      </div>
    </button><StaffProfiles people={activity.staff} /></article>
  );
};

export default ActivityCard;
